import { LineChart } from '@mui/x-charts/LineChart';
import type { CostHistoryEntry, CostHistoryResponse } from '../../../data/cost-history';
import { preciseCurrencyFormatter } from '../../../helpers/format-currency';
import { yAxisBounds } from '../../../helpers/y-axis-bounds';
import { currentYearMonth, monthLabel } from '../../../helpers/year-month';

// Trailing window (in months) the low-callout searches, shared by the default
// window and the callout label so the two never drift.
const LOW_WINDOW_MONTHS = 12;

// CostMetric selects which cost tier the chart plots so it matches the tier the
// popover's table already shows for this product: the deals indicator shows the
// frontline (single-case) cost, the volume indicator shows the deepest-tier
// (lowest-achievable) cost. Plotting the same tier keeps the table, the line,
// and the callout in agreement.
export type CostMetric = 'frontline' | 'lowest';

export function costOf(entry: CostHistoryEntry, metric: CostMetric): number {
  return metric === 'frontline' ? entry.frontline_cost : entry.lowest_achievable;
}

export function toCostHistorySeries(
  response: CostHistoryResponse,
  metric: CostMetric
): { months: string[]; values: number[] } {
  return {
    months: response.series.map((e) => monthLabel(e.year_month)),
    values: response.series.map((e) => costOf(e, metric)),
  };
}

// lowInWindow finds the cheapest month among the most recent `window` non-future
// months, returning its cost, month, and index into the full series so the chart
// can mark the same point the callout names. Returns null when there is no
// non-future data, or when the window is flat to the cent: a stable price has no
// meaningful "low", and marking any month would be arbitrary and inconsistent
// (sub-cent float noise otherwise lands the low on a random month). When there is
// a real low, ties resolve to the most recent month at that price.
export function lowInWindow(
  response: CostHistoryResponse,
  metric: CostMetric,
  nowYm: number,
  window = LOW_WINDOW_MONTHS
): { cost: number; year_month: number; index: number } | null {
  const past = response.series
    .map((e, index) => ({ cost: costOf(e, metric), year_month: e.year_month, index }))
    .filter((e) => e.year_month <= nowYm);

  const recent = past.slice(-window);
  if (recent.length === 0) {
    return null;
  }

  // Compare on whole cents — prices are shown to the cent, so anything finer is
  // display-invisible noise, not a real dip.
  const cents = (cost: number) => Math.round(cost * 100);
  const lowCents = Math.min(...recent.map((e) => cents(e.cost)));
  const highCents = Math.max(...recent.map((e) => cents(e.cost)));
  if (lowCents === highCents) {
    return null;
  }

  let low = recent[0];
  for (const e of recent) {
    if (cents(e.cost) <= cents(low.cost)) {
      low = e;
    }
  }

  return low;
}

export function formatLowCallout(low: { cost: number; year_month: number } | null): string | null {
  if (!low) {
    return null;
  }

  return `Lowest in ${LOW_WINDOW_MONTHS} mo: ${preciseCurrencyFormatter(low.cost)} (${monthLabel(low.year_month)})`;
}

export function CostHistoryChart({
  months,
  values,
  lowIndex,
}: {
  months: string[];
  values: number[];
  lowIndex: number;
}) {
  const { min, max } = yAxisBounds(values);

  return (
    <LineChart
      height={140}
      // MUI reserves axis space via each axis's own width/height, and `margin` is
      // extra space OUTSIDE that. So the way to kill the dead gutter is a small
      // explicit yAxis.width (not a bigger margin), mirroring the dashboard chart.
      // `band` scale insets the first/last month off the frame, so the newest
      // point isn't squished against the edge.
      xAxis={[{ scaleType: 'band', data: months, height: 20, tickLabelStyle: { fontSize: 11 } }]}
      yAxis={[{ min, max, width: 36, tickNumber: 4, tickLabelStyle: { fontSize: 11 } }]}
      series={[
        {
          data: values,
          // Posted prices hold flat within a month then step at the next
          // posting; a smooth curve would imply movement that never happened.
          curve: 'stepAfter',
          color: 'var(--color-status-muted)',
          showMark: ({ index }) => index === lowIndex,
          valueFormatter: (v) => (v == null ? '' : preciseCurrencyFormatter(v)),
        },
      ]}
      margin={{ top: 8, bottom: 0, left: 0, right: 8 }}
      sx={{
        // Match the savings indicator's "low / good deal" green on the marked
        // cheapest month, ringed in the popover surface so it reads as a pin.
        '& .MuiLineElement-mark': {
          fill: 'var(--color-green-600)',
          stroke: 'var(--color-popover)',
          strokeWidth: 2,
        },
      }}
    />
  );
}

export function CostHistorySection({
  costHistory,
  loading,
  metric,
}: {
  costHistory?: CostHistoryResponse;
  loading: boolean;
  metric: CostMetric;
}) {
  if (loading) {
    return (
      <div
        data-testid="cost-history-loading"
        className="mt-2 h-[140px] animate-pulse rounded bg-muted"
      />
    );
  }

  if (!costHistory || costHistory.series.length < 2) {
    return null; // single point can't draw a trend; empty degrades to the table only
  }

  const { months, values } = toCostHistorySeries(costHistory, metric);
  const low = lowInWindow(costHistory, metric, currentYearMonth());
  const callout = formatLowCallout(low);

  return (
    <div className="mt-2 border-t border-popover-border pt-2">
      <CostHistoryChart months={months} values={values} lowIndex={low?.index ?? -1} />
      {callout && (
        <div className="mt-1.5 text-center text-sm font-semibold text-popover-foreground">
          {callout}
        </div>
      )}
    </div>
  );
}
